import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  MenuItem,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { backend_api } from "../../../../static/server";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllRoles,
  getAllUsers,
} from "../../../../redux/Apps/users/userSlice";
import { useDialog } from "../../../../components/DialogContext";
import { debounce } from "../../../../styles/styles";

export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const ROLE_DISPLAY_ORDER = [
  "Attorney",
  "Non-Attorney",
  "Administrator",
  "Billing",
  "General Access",
  "Banker",
  "Accounts",
  "Reports",
];

const InviteUserForm = () => {
  const dispatch = useDispatch();
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userExist, setUserExist] = useState(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const { token, teamMemberLawyerId } = useSelector((data) => data.user);
  const { allRoles } = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(
      getAllRoles({
        page: 1,
        token: token,
      })
    );
  }, [token, dispatch]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      first_name: "",
      reminder_mail: "",
      keywords: "attorney",
      access: [],
    },
  });
  const selectedAccess = watch("access");

  const sortedRoles = useMemo(() => {
    const priority = new Map(
      ROLE_DISPLAY_ORDER.map((roleName, index) => [roleName, index]),
    );

    return [...(allRoles || [])].sort((left, right) => {
      const leftRank = priority.get(left?.role_name) ?? 999;
      const rightRank = priority.get(right?.role_name) ?? 999;

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return (left?.role_name || "").localeCompare(right?.role_name || "");
    });
  }, [allRoles]);

  useEffect(() => {
    const hasSelectedRole =
      Array.isArray(selectedAccess) && selectedAccess.length > 0;
    if (hasSelectedRole) {
      return;
    }

    const defaultRole = sortedRoles.find(
      (role) => role?.role_name === "General Access",
    );

    if (defaultRole?.id) {
      setValue("access", [defaultRole.id], {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [selectedAccess, setValue, sortedRoles]);

  const handleCheckEmail = useCallback(
    debounce(async (email) => {
      if (!email || !EMAIL_REGEX.test(email)) {
        setUserExist(null);
        return;
      }

      setIsCheckingEmail(true);
      try {
        const response = await axios.get(
          `${backend_api}user/check-mail/?email=${email}`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              ...(token && { Authorization: `Token ${token}` }),
            },
          }
        );

        if (response?.data?.exist) {
          setUserExist(response.data.exist);
          toast.info("Email already exists");
        } else {
          setUserExist(false);
        }
      } catch (error) {
        console.error("error in check email exists:", error);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 300),
    [token]
  );

  const onSubmit = async (data) => {
    if (!EMAIL_REGEX.test(data.reminder_mail)) {
      toast.error("Invalid email address.");
      return;
    }

    if (userExist) {
      toast.error("Cannot invite user - email already exists.");
      return;
    }

    if (!data.reminder_mail.trim()) {
      toast.error("Email is required.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(
        `${backend_api}team/create-employee/`,
        { ...data, is_active: false },
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.status === 200 || res.status === 201) {
        toast.success("Employee invited successfully!");
        reset();
        closeDialog();
        dispatch(
          getAllUsers({
            token: token,
            isActive: "True",
            teamMemberLawyerId,
          })
        );
      }
    } catch (error) {
      toast.error("Failed to invite user");
      console.error("Error inviting user:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "1rem",
            p: "1rem",
          }}
        >
          <Controller
            name="first_name"
            control={control}
            rules={{
              maxLength: {
                value: 50,
                message: "First name must be less than 50 characters",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="First Name"
                size="small"
                fullWidth
                error={!!errors.first_name}
                helperText={errors.first_name?.message}
              />
            )}
          />
          <Controller
            name="reminder_mail"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: EMAIL_REGEX,
                message: "Enter a valid email address",
              },
              maxLength: {
                value: 254,
                message: "Email must be less than 254 characters",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={
                  <span>
                    Email
                    <span style={{ color: "red" }}>*</span>
                  </span>
                }
                size="small"
                fullWidth
                error={!!errors.reminder_mail || userExist === true}
                onChange={(e) => {
                  field.onChange(e);
                  handleCheckEmail(e.target.value);
                }}
                helperText={
                  errors.reminder_mail?.message ||
                  (userExist === true ? "Email already exists" : "") ||
                  (isCheckingEmail ? "Checking email..." : "")
                }
              />
            )}
          />
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="keywords"
              control={control}
              rules={{ required: "Profile role is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Profile Role"
                  size="small"
                  fullWidth
                  error={!!errors.keywords}
                  helperText={errors.keywords?.message}
                >
                  <MenuItem value="attorney">Attorney</MenuItem>
                  <MenuItem value="non-attorney">Non-Attorney</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="access"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    options={sortedRoles}
                    getOptionLabel={(option) => option?.role_name || ""}
                    value={
                      sortedRoles?.filter((option) =>
                        field.value?.includes(option.id)
                      ) || []
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue.map((item) => item.id))
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User Role"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </Box>
        </Box>
        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
            bgcolor: "rgba(255, 255, 255, 0.8)",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={closeDialog}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting || isCheckingEmail}
            variant="contained"
            color="primary"
            type="submit"
            size="small"
            disabled={userExist === true}
          >
            Send Invite
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default InviteUserForm;
